import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import VocalRangeSlider from '../../../components/ui/VocalRangeSlider';
import VoiceTypeSlider from '../../../components/ui/VoiceTypeSlider';
import RoleTypeSlider from '../../../components/ui/RoleTypeSlider';
import AgeRangeSlider from '../../../components/ui/AgeRangeSlider';
import NewMusicalModal from '../../../components/ui/NewMusicalModal';
import RoleService from '../../../services/roleService';
import MusicalService from '../../../services/musicalService';
import { useAuth } from '../../../contexts/AuthContext';

const AddCharacterModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Musical search state
  const [musicalSearchTerm, setMusicalSearchTerm] = useState('');
  const [musicalSearchResults, setMusicalSearchResults] = useState([]);
  const [showMusicalResults, setShowMusicalResults] = useState(false);
  const [loadingMusicalSearch, setLoadingMusicalSearch] = useState(false);
  const [showCreateMusicalModal, setShowCreateMusicalModal] = useState(false);
  const [musicalNotFound, setMusicalNotFound] = useState(false);

  // Slider state
  const [voiceType, setVoiceType] = useState('bass');
  const [roleType, setRoleType] = useState('lead');
  const [ageMin, setAgeMin] = useState(10);
  const [ageMax, setAgeMax] = useState(65);
  
  // Vocal range state - Default to Bass range (E2-E4), independent of Voice Type
  const [vocalRangeMin, setVocalRangeMin] = useState('E2');
  const [vocalRangeMax, setVocalRangeMax] = useState('E4');

  // Form state
  const [formData, setFormData] = useState({
    character_name: '',
    musical_id: '',
    musical_title: '',
    description: ''
  });

  // Reset modal state when opened/closed
  useEffect(() => {
    if (isOpen) {
      resetFormState();
    }
  }, [isOpen]);

  const resetFormState = () => {
    setFormData({
      character_name: '',
      musical_id: '',
      musical_title: '',
      description: ''
    });
    setMusicalSearchTerm('');
    setMusicalSearchResults([]);
    setShowMusicalResults(false);
    setMusicalNotFound(false);
    // Set default vocal range to Bass (E2-E4) - fixed independent values
    setVocalRangeMin('E2');
    setVocalRangeMax('E4');
    setVoiceType('bass');
    setRoleType('lead');
    setAgeMin(10);
    setAgeMax(65);
    setError(null);
    setSuccess(false);
  };

  // Handle musical search
  const handleMusicalSearch = async (searchTerm) => {
    setMusicalSearchTerm(searchTerm);
    setMusicalNotFound(false);
    
    if (!searchTerm?.trim()) {
      setMusicalSearchResults([]);
      setShowMusicalResults(false);
      return;
    }

    setLoadingMusicalSearch(true);
    try {
      const { data, error } = await MusicalService?.searchMusicalsForModal(searchTerm?.trim());
      if (error) {
        setError('Failed to search musicals');
      } else {
        setMusicalSearchResults(data || []);
        setShowMusicalResults(true);
        
        // Check if no results found
        if (data?.length === 0) {
          setMusicalNotFound(true);
        }
      }
    } catch (err) {
      setError('Failed to search musicals');
      setMusicalSearchResults([]);
    } finally {
      setLoadingMusicalSearch(false);
    }
  };

  const handleMusicalSelect = (musical) => {
    setFormData(prev => ({
      ...prev,
      musical_id: musical?.id,
      musical_title: musical?.title
    }));
    setMusicalSearchTerm(musical?.title);
    setShowMusicalResults(false);
    setMusicalNotFound(false);
  };

  const handleMusicalInputKeyPress = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      handleMusicalSearch(musicalSearchTerm);
    }
  };

  const handleCreateNewMusical = () => {
    setShowCreateMusicalModal(true);
  };

  const handleNewMusicalCreated = (newMusical) => {
    setFormData(prev => ({
      ...prev,
      musical_id: newMusical?.id,
      musical_title: newMusical?.title
    }));
    setMusicalSearchTerm(newMusical?.title);
    setShowCreateMusicalModal(false);
    setMusicalNotFound(false);
    setShowMusicalResults(false);
  };

  const handleVocalRangeChange = (minNote, maxNote) => {
    setVocalRangeMin(minNote);
    setVocalRangeMax(maxNote);
  };

  const handleVoiceTypeChange = (newVoiceType) => {
    // Simply update voice type - no automatic vocal range updates
    setVoiceType(newVoiceType);
  };

  const handleRoleTypeChange = (newRoleType) => {
    setRoleType(newRoleType);
  };

  const handleAgeRangeChange = (minAge, maxAge) => {
    setAgeMin(minAge);
    setAgeMax(maxAge);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.character_name?.trim()) {
      setError('Character name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = {
        character_name: formData?.character_name?.trim(),
        musical_id: formData?.musical_id || null,
        description: formData?.description?.trim() || null,
        vocal_range: `${vocalRangeMin}-${vocalRangeMax}`,
        voice_type: voiceType,
        role_type: roleType,
        age_range: `${ageMin}-${ageMax}`,
        is_principal: roleType === 'lead'
      };

      // Remove the second parameter since createRole will use auth session directly
      const { data, error } = await RoleService?.createRole(submitData);

      if (error) {
        // Handle specific authentication errors
        if (error?.code === '42501') {
          setError('Permission denied. Please refresh the page and try logging in again.');
        } else {
          setError(error?.message || 'Failed to create character');
        }
      } else {
        setSuccess(true);
        // Enhanced success callback to ensure search refreshes properly
        setTimeout(() => {
          onSuccess?.(data);
        }, 1200); // Increased delay to ensure database write completion
      }
    } catch (err) {
      console.error('Error creating character:', err);
      setError('An unexpected error occurred. Please refresh the page and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white border-opacity-20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Icon name="UserPlus" size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Add New Character</h2>
                <p className="text-sm text-blue-100">Create a new character role</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-blue-100" />
            </button>
          </div>

          {/* Success State */}
          {success && (
            <div className="p-6 border-b border-white border-opacity-20">
              <div className="flex items-center space-x-3 p-4 bg-green-600 bg-opacity-20 border border-green-400 border-opacity-30 rounded-lg">
                <Icon name="CheckCircle" size={20} className="text-green-400" />
                <div>
                  <p className="font-medium text-green-100">Character Created Successfully!</p>
                  <p className="text-sm text-green-200">The character has been added to the database.</p>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-6 border-b border-white border-opacity-20">
              <div className="flex items-center space-x-3 p-4 bg-red-600 bg-opacity-20 border border-red-400 border-opacity-30 rounded-lg">
                <Icon name="AlertTriangle" size={20} className="text-red-400" />
                <div>
                  <p className="font-medium text-red-100">Error</p>
                  <p className="text-sm text-red-200">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          {!success && (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Character Name - Required */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2">
                  Character Name *
                </label>
                <Input
                  type="text"
                  value={formData?.character_name}
                  onChange={(e) => handleInputChange('character_name', e?.target?.value)}
                  placeholder="Enter character name"
                  required
                  disabled={loading}
                  className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder:text-blue-200"
                />
              </div>

              {/* Musical Search */}
              <div className="relative">
                <label className="block text-sm font-medium text-blue-100 mb-2">
                  Musical
                </label>
                <Input
                  type="text"
                  value={musicalSearchTerm}
                  onChange={(e) => setMusicalSearchTerm(e?.target?.value)}
                  onKeyPress={handleMusicalInputKeyPress}
                  onFocus={() => {
                    if (musicalSearchResults?.length > 0) {
                      setShowMusicalResults(true);
                    }
                  }}
                  placeholder="Search for musical"
                  disabled={loading}
                  className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder:text-blue-200"
                />
                {loadingMusicalSearch && (
                  <div className="absolute right-3 top-11 transform -translate-y-1/2">
                    <Icon name="Loader2" size={16} className="text-blue-200 animate-spin" />
                  </div>
                )}
                
                {/* Musical Not Found - Ask to Create */}
                {musicalNotFound && musicalSearchTerm?.trim() && !showMusicalResults && (
                  <div className="absolute z-10 w-full mt-1 bg-yellow-800 bg-opacity-90 border border-yellow-600 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <Icon name="AlertCircle" size={20} className="text-yellow-400" />
                      <div className="flex-1">
                        <p className="text-yellow-100 font-medium">Musical not found</p>
                        <p className="text-yellow-200 text-sm">"{musicalSearchTerm}" is not in our database.</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 mt-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCreateNewMusical}
                        className="border-yellow-400 text-yellow-100 hover:bg-yellow-600 hover:bg-opacity-20"
                        iconName="Plus"
                        iconPosition="left"
                      >
                        Create New Musical
                      </Button>
                      <button
                        type="button"
                        onClick={() => setMusicalNotFound(false)}
                        className="text-yellow-200 hover:text-yellow-100 text-sm"
                      >
                        Continue without musical
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Search Results Dropdown */}
                {showMusicalResults && musicalSearchResults?.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {musicalSearchResults?.map(musical => (
                      <button
                        key={musical?.id}
                        type="button"
                        onClick={() => handleMusicalSelect(musical)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-700 focus:bg-gray-700 focus:outline-none border-b border-gray-600 last:border-b-0"
                      >
                        <div className="text-white font-medium">{musical?.title}</div>
                        {(musical?.composer || musical?.year_premiered) && (
                          <div className="text-gray-300 text-sm">
                            {musical?.composer && `by ${musical?.composer}`}
                            {musical?.composer && musical?.year_premiered && ' • '}
                            {musical?.year_premiered && `${musical?.year_premiered}`}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  placeholder="Character description, personality, etc."
                  disabled={loading}
                  rows={3}
                  className="w-full px-3 py-2 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-white placeholder:text-blue-200"
                />
              </div>

              {/* Voice Type Slider */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2">
                  Voice Type
                </label>
                <VoiceTypeSlider
                  value={voiceType}
                  onChange={handleVoiceTypeChange}
                  disabled={loading}
                  className="w-full"
                />
              </div>

              {/* Role Type Slider */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2">
                  Role Type
                </label>
                <RoleTypeSlider
                  value={roleType}
                  onChange={handleRoleTypeChange}
                  disabled={loading}
                  className="w-full"
                />
              </div>

              {/* Vocal Range Slider */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2">
                  Vocal Range
                </label>
                <VocalRangeSlider
                  minValue={vocalRangeMin}
                  maxValue={vocalRangeMax}
                  onChange={handleVocalRangeChange}
                  disabled={loading}
                  className="w-full"
                />
                <p className="text-xs text-blue-200 mt-2">
                  Current range: {vocalRangeMin} to {vocalRangeMax}
                </p>
              </div>

              {/* Age Range Slider */}
              <div>
                <label className="block text-sm font-medium text-blue-100 mb-2">
                  Age Range
                </label>
                <AgeRangeSlider
                  minAge={ageMin}
                  maxAge={ageMax}
                  onChange={handleAgeRangeChange}
                  disabled={loading}
                  className="w-full"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-white border-opacity-20">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                  className="border-white border-opacity-30 text-blue-100 hover:bg-white hover:bg-opacity-20"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  iconName={loading ? "Loader2" : "UserPlus"}
                  iconPosition="left"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0"
                >
                  {loading ? 'Creating...' : 'Create Character'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* New Musical Creation Modal */}
      <NewMusicalModal
        isOpen={showCreateMusicalModal}
        onClose={() => setShowCreateMusicalModal(false)}
        onSuccess={handleNewMusicalCreated}
        prefilledTitle={musicalSearchTerm}
      />
    </>
  );
};

export default AddCharacterModal;